import type { TaskListCatalog } from '@techgitter/contracts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY = 'task_lists_cache_v1';
const TTL_MS = 86_400_000; // 24 hours
const SCHEMA_VERSION = 1;
const API_URL = '/api/task-lists';
const CACHE_CLEAR_URL = '/api/task-lists/cache/clear';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEnvelope {
  cachedAt: number;
  ttlMs: number;
  schemaVersion: number;
  data: TaskListCatalog;
}

export interface GetTaskListsOptions {
  forceRefresh?: boolean;
  allowStale?: boolean;
}

export interface GetTaskListsResult {
  data: TaskListCatalog;
  isStale?: boolean;
  warning?: string;
}

export interface RefreshResult {
  success: boolean;
  data?: TaskListCatalog;
  error?: string;
}

export interface InvalidateResult {
  success: boolean;
  serverCleared: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Module-level state (in-memory tier)
// ---------------------------------------------------------------------------

let memoryCache: CacheEnvelope | null = null;
let inflightRequest: Promise<GetTaskListsResult> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isFresh(envelope: CacheEnvelope): boolean {
  return Date.now() - envelope.cachedAt < envelope.ttlMs;
}

function readLocalStorage(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CacheEnvelope = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (!parsed.data || !parsed.cachedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(envelope: CacheEnvelope): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full or unavailable
  }
}

function buildEnvelope(data: TaskListCatalog): CacheEnvelope {
  return {
    cachedAt: Date.now(),
    ttlMs: TTL_MS,
    schemaVersion: SCHEMA_VERSION,
    data,
  };
}

function storeEnvelope(envelope: CacheEnvelope): void {
  memoryCache = envelope;
  writeLocalStorage(envelope);
}

function resolveCache(): CacheEnvelope | null {
  if (memoryCache) return memoryCache;
  const stored = readLocalStorage();
  if (stored) {
    memoryCache = stored;
  }
  return stored;
}

async function fetchFromNetwork(): Promise<TaskListCatalog> {
  const response = await fetch(API_URL);
  if (!response.ok) {
    throw new Error(`Task lists fetch failed: ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function getTaskLists(
  options?: GetTaskListsOptions,
): Promise<GetTaskListsResult> {
  const { forceRefresh = false, allowStale = true } = options ?? {};

  if (!forceRefresh) {
    const cached = resolveCache();
    if (cached && isFresh(cached)) {
      return { data: cached.data };
    }
  }

  if (inflightRequest && !forceRefresh) {
    return inflightRequest;
  }

  const networkPromise = (async (): Promise<GetTaskListsResult> => {
    try {
      const data = await fetchFromNetwork();
      const envelope = buildEnvelope(data);
      storeEnvelope(envelope);
      return { data };
    } catch (err: any) {
      if (allowStale) {
        const stale = resolveCache();
        if (stale) {
          return {
            data: stale.data,
            isStale: true,
            warning: `Using cached task lists (network error: ${err.message})`,
          };
        }
      }
      throw err;
    } finally {
      inflightRequest = null;
    }
  })();

  inflightRequest = networkPromise;
  return networkPromise;
}

export async function refreshTaskListsCache(): Promise<RefreshResult> {
  try {
    // Clear server-side cache first so we get fresh data from blob storage
    try {
      await fetch(CACHE_CLEAR_URL, { method: 'POST' });
    } catch {
      // Best-effort — continue even if server cache clear fails
    }

    const data = await fetchFromNetwork();
    const envelope = buildEnvelope(data);
    storeEnvelope(envelope);
    return { success: true, data };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function invalidateTaskListsCache(): Promise<InvalidateResult> {
  memoryCache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }

  try {
    const res = await fetch(CACHE_CLEAR_URL, { method: 'POST' });
    const data = await res.json();
    return { success: true, serverCleared: data.cleared };
  } catch (error: any) {
    return { success: true, serverCleared: false, error: error.message };
  }
}

// ---------------------------------------------------------------------------
// Cache metadata
// ---------------------------------------------------------------------------

export function getTaskListsCacheMetadata(): {
  cachedAt: number;
  ttlMs: number;
  recordCount: number;
} | null {
  const envelope = resolveCache();
  if (!envelope) return null;
  return {
    cachedAt: envelope.cachedAt,
    ttlMs: envelope.ttlMs,
    recordCount: envelope.data.taskLists.length,
  };
}

// ---------------------------------------------------------------------------
// Test helpers
// ---------------------------------------------------------------------------

/** @internal Reset all module state for tests. */
export function _resetForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** @internal Clear only in-memory cache. */
export function _clearMemoryForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
}
